import { isDevMode } from '@angular/core';

// Persisted opt-in for the dev-only gameplay test tooling (the "instant
// complete" buttons). Enable from any URL with `?devtools=1`, disable with
// `?devtools=0`. It is persisted because the gameplay launcher navigation
// strips query params, so a one-shot read on the gameplay route wouldn't see
// it — and so the opt-in survives reloads.
const DEVTOOLS_STORAGE_KEY = 'ap:devtools';

/**
 * Whether dev gameplay test tooling is available for the current user. Always
 * on in a dev build; in production, on for anyone who has opted in via
 * `?devtools=1` (persisted, see {@link captureDevToolsFlag}).
 */
export function devTestToolsEnabled(): boolean {
  if (isDevMode()) {
    return true;
  }
  try {
    return localStorage.getItem(DEVTOOLS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Captures the `?devtools=` flag from a URL (or raw query string) into
 * persistent storage: `1`/`true` enables the tooling, `0`/`false`/empty clears
 * it, the param being absent is a no-op. Call on every navigation so the opt-in
 * survives the launcher hop and page reloads.
 */
export function captureDevToolsFlag(urlOrSearch: string): void {
  const query = urlOrSearch.includes('?')
    ? urlOrSearch.slice(urlOrSearch.indexOf('?') + 1)
    : urlOrSearch;
  const value = new URLSearchParams(query).get('devtools');
  if (value === null) {
    return;
  }
  const normalized = value.trim().toLowerCase();
  try {
    if (normalized === '0' || normalized === 'false' || normalized === '') {
      localStorage.removeItem(DEVTOOLS_STORAGE_KEY);
    } else {
      localStorage.setItem(DEVTOOLS_STORAGE_KEY, '1');
    }
  } catch {
    // Ignore storage errors in private/restricted browsing contexts.
  }
}
